import { mapContentRefs } from '@gitbook/doc-core';
import type { Document } from '@gitbook/slate';

import type { ContentReference } from '../types';

export interface DuplicateContentParams {
    /**
     * A map of URLs to content refs that will be replaced in the content.
     * The keys are the URLs to replace, and the values are the content refs to use.
     */
    contentRefs?: Record<string, ContentReference>;
    /**
     * A map of space IDs to space IDs: references to a key space are re-pointed to the value space.
     * Duplication preserves page, file and reusable content IDs, so the references stay valid.
     */
    spaces?: Record<string, string>;
    /**
     * If true, will also set meta links to the original pages during the duplication
     */
    withMetaLinks?: boolean;
}

/**
 * Apply params to a document while duplicating it.
 *
 * This function travels through the document, through each block, and replaces
 * any content references that it finds.
 */
export function applyDuplicateContentParams(
    document: Document,
    params: DuplicateContentParams | undefined
): Document {
    if (!params?.contentRefs && !params?.spaces) {
        return document;
    }

    return mapContentRefs(document, (_node, ref) => applyDuplicateContentRefParams(ref, params));
}

/**
 * Apply params to a content reference while duplicating it.
 */
export function applyDuplicateContentRefParams(
    ref: ContentReference,
    params: DuplicateContentParams | undefined
): ContentReference {
    if (ref.kind === 'url' && params?.contentRefs?.[ref.url]) {
        // If the ref is a URL and we have a replacement for it, return the new ref.
        return params.contentRefs[ref.url];
    }

    const space = typeof ref.space === 'string' ? params?.spaces?.[ref.space] : undefined;
    if (space) {
        return { ...ref, space };
    }

    return ref;
}
